import React, { useEffect, useState } from 'react';
import { useAuth } from '../lib/AuthContext';
import { useT } from '../lib/i18n';
import { api, API, getToken } from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Sparkles, LogOut, UserCircle, Check, Volume2, CreditCard, Languages, Lock } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

export default function Settings() {
    const { user, logout, refresh } = useAuth();
    const { t, lang, setLang } = useT();
    const navigate = useNavigate();
    const location = useLocation();

    const [voices, setVoices] = useState([]);
    const [plans, setPlans] = useState([]);
    const [paying, setPaying] = useState(null);
    const [payStatus, setPayStatus] = useState(null);
    const [previewing, setPreviewing] = useState(null);
    const [cancelBusy, setCancelBusy] = useState(false);

    const cancelSubscription = async () => {
        if (!window.confirm(t('settings.cancel.confirm'))) return;
        setCancelBusy(true);
        try {
            await api.post('/payments/cancel');
            await refresh();
            toast.success(t('settings.cancel.scheduled'));
        } catch (err) {
            toast.error(err.response?.data?.detail || t('settings.cancel.error'));
        } finally {
            setCancelBusy(false);
        }
    };

    useEffect(() => {
        api.get(`/voice/voices?language=${lang}`).then(r => setVoices(r.data));
        api.get(`/payments/plans?language=${lang}`).then(r => setPlans(r.data));
    }, [lang]);

    // Handle return from Stripe checkout
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const sid = params.get('session_id');
        if (!sid) return;
        let cancelled = false;
        const poll = async (attempt = 0) => {
            if (cancelled || attempt > 10) return;
            try {
                const { data } = await api.get(`/payments/status/${sid}`);
                if (data.payment_status === 'paid') {
                    await refresh();
                    setPayStatus('success');
                    toast.success(t('settings.payment.success'));
                    navigate('/app/konto', { replace: true });
                    return;
                }
                if (data.status === 'expired') {
                    setPayStatus('expired');
                    toast.error(t('settings.payment.expired'));
                    navigate('/app/konto', { replace: true });
                    return;
                }
                setPayStatus('pending');
                setTimeout(() => poll(attempt + 1), 2000);
            } catch {
                setTimeout(() => poll(attempt + 1), 2000);
            }
        };
        poll();
        return () => { cancelled = true; };
    }, [location.search, refresh, navigate]);

    const setVoice = async (voiceKey) => {
        await api.post('/voice/preference', { voice: voiceKey });
        await refresh();
        toast.success(t('settings.voice.updated'));
    };

    const previewVoice = async (voiceKey) => {
        if (previewing === voiceKey) return;
        setPreviewing(voiceKey);
        try {
            const res = await fetch(`${API}/voice/speak`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
                body: JSON.stringify({ text: lang === 'en' ? 'Hi. I’m here to listen. What’s on your mind right now?' : 'Hei. Jeg er her for å lytte. Hva tenker du på akkurat nå?', voice: voiceKey }),
            });
            const blob = await res.blob();
            const audio = new Audio(URL.createObjectURL(blob));
            audio.onended = () => setPreviewing(null);
            await audio.play();
        } catch {
            setPreviewing(null);
            toast.error(t('settings.voice.preview.error'));
        }
    };

    const startCheckout = async (planKey) => {
        setPaying(planKey);
        try {
            const { data } = await api.post('/payments/checkout', {
                plan: planKey,
                origin_url: window.location.origin,
                language: lang,
            });
            // Break out of any preview iframe so Stripe can render at top-level
            try { window.top.location.href = data.url; }
            catch { window.location.href = data.url; }
        } catch (e) {
            toast.error(e.response?.data?.detail || t('settings.payment.error'));
        } finally {
            setPaying(null);
        }
    };

    const startTrial = async () => {
        try {
            await api.post('/trial/start');
            await refresh();
            toast.success(t('settings.trial.activated'));
        } catch (e) {
            toast.error(e.response?.data?.detail || t('settings.trial.error'));
        }
    };

    const currentPlan = user?.plan;
    const expires = user?.plan_expires_at ? new Date(user.plan_expires_at).toLocaleDateString(lang === 'en' ? 'en-US' : 'nb-NO') : null;

    const groupedVoices = {
        female: voices.filter(v => v.gender === 'female'),
        male: voices.filter(v => v.gender === 'male'),
        neutral: voices.filter(v => v.gender === 'neutral'),
    };

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10 pb-24" data-testid="settings-page">
            <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{t('settings.title')}</h1>

            {payStatus === 'pending' && (
                <div className="mt-4 p-3 rounded-xl bg-sage/10 border border-sage/30 text-sm text-sage-dark" data-testid="pay-pending">
                    {t('settings.payment.processing')}
                </div>
            )}

            <div className="mt-6 rounded-2xl border border-sand bg-white p-5">
                <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full bg-sage-soft flex items-center justify-center text-sage-dark">
                        <UserCircle className="w-5 h-5"/>
                    </div>
                    <div className="min-w-0">
                        <p className="font-medium text-ink truncate" data-testid="settings-alias">{user?.alias}</p>
                        <p className="text-sm text-inkSoft truncate" data-testid="settings-email">{user?.email}</p>
                    </div>
                </div>
                <p className="text-xs text-inkSoft mt-3">{t('settings.auth.method')} {user?.auth_method === 'google' ? t('settings.auth.google') : user?.auth_method === 'anonymous' ? t('settings.auth.anonymous') : t('settings.auth.email')}</p>
                {user?.auth_method === 'anonymous' && (
                    <LinkEmailCard onLinked={refresh} />
                )}
            </div>

            {/* Language toggle */}
            <div className="mt-4 rounded-2xl border border-sand bg-white p-5" data-testid="language-section">
                <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-full bg-sage-soft flex items-center justify-center text-sage-dark">
                        <Languages className="w-4 h-4"/>
                    </div>
                    <h2 className="font-display font-semibold text-ink">{t('settings.language')}</h2>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <button
                        onClick={() => setLang('no')}
                        className={`p-3 rounded-xl border-2 text-sm font-medium transition-all ${lang === 'no' ? 'border-sage bg-sage text-bone' : 'border-sand bg-bone text-ink hover:border-sage/40'}`}
                        data-testid="lang-no-btn"
                    >
                        🇳🇴 {t('settings.language.no')}
                    </button>
                    <button
                        onClick={() => setLang('en')}
                        className={`p-3 rounded-xl border-2 text-sm font-medium transition-all ${lang === 'en' ? 'border-sage bg-sage text-bone' : 'border-sand bg-bone text-ink hover:border-sage/40'}`}
                        data-testid="lang-en-btn"
                    >
                        🇺🇸 {t('settings.language.en')}
                    </button>
                </div>
            </div>

            {/* Pilot-tilbakemelding — vises øverst for synlighet */}
            <div className="mt-6 rounded-2xl border border-sage/30 bg-sage/5 p-5 flex items-start gap-4" data-testid="pilot-feedback-cta">
                <div className="flex-1">
                    <p className="text-sm font-medium text-ink">{t('settings.pilot.title')}</p>
                    <p className="text-xs text-inkSoft mt-1 leading-relaxed">
                        {t('settings.pilot.body')}
                    </p>
                </div>
                <Button
                    onClick={() => navigate('/app/tilbakemelding')}
                    size="sm"
                    className="rounded-full bg-sage hover:bg-sage-dark text-bone shrink-0"
                    data-testid="pilot-feedback-btn"
                >
                    {t('settings.pilot.btn')}
                </Button>
            </div>

            {/* Plans */}
            <h2 className="font-display text-lg font-semibold mt-10 flex items-center gap-2">
                <CreditCard className="w-4 h-4"/> {t('settings.membership')}
            </h2>
            {currentPlan && (
                <p className="text-sm text-sage-dark mt-1" data-testid="current-plan">
                    {t('settings.plan.active')}: <span className="font-medium">{currentPlan === 'basic' ? t('settings.plan.basic') : currentPlan === 'trial' ? t('settings.plan.trial') : t('settings.plan.premium')}</span>
                    {expires && <> · {t('settings.plan.until')} {expires}</>}
                    {user?.on_trial && user?.trial_hours_left != null && (
                        <> · <span className="font-medium">{Math.max(0, Math.round(user.trial_hours_left))} {t('settings.trial.hours.left')}</span></>
                    )}
                </p>
            )}
            {user?.stripe_subscription_id && !user?.cancel_at_period_end && (
                <Button
                    onClick={cancelSubscription}
                    disabled={cancelBusy}
                    variant="outline"
                    size="sm"
                    className="mt-3 rounded-full border-sand text-inkSoft hover:bg-sand/40"
                    data-testid="cancel-subscription-btn"
                >
                    {cancelBusy ? t('settings.cancel.busy') : t('settings.cancel.btn')}
                </Button>
            )}
            {user?.cancel_at_period_end && (
                <p className="text-xs text-inkSoft mt-2" data-testid="cancel-scheduled-notice">
                    {t('settings.cancel.scheduled.notice')}
                </p>
            )}
            {!currentPlan && !user?.trial_used && (
                <div className="mt-4 rounded-2xl border border-sage/30 bg-sage/5 p-4 flex items-center justify-between gap-3" data-testid="trial-offer-card">
                    <div className="min-w-0">
                        <p className="font-display font-semibold text-ink">{t('settings.trial.offer.title')}</p>
                        <p className="text-xs text-inkSoft mt-0.5">{t('settings.trial.offer.body')}</p>
                    </div>
                    <Button onClick={startTrial} className="shrink-0 rounded-full bg-sage hover:bg-sage-dark text-bone" data-testid="settings-start-trial">
                        <Sparkles className="w-4 h-4 mr-2"/> {t('common.start')}
                    </Button>
                </div>
            )}
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
                {plans.map(p => {
                    const active = currentPlan === p.key;
                    const isUsd = p.currency === 'usd';
                    const priceLabel = isUsd ? `$${p.amount.toFixed(0)}/mo` : `${p.amount} kr/mnd`;
                    const priceMain = isUsd ? `$${p.amount.toFixed(0)}` : p.amount;
                    return (
                        <div key={p.key} className={`rounded-2xl border p-5 ${active ? 'border-sage bg-sage/5' : 'border-sand bg-white'}`} data-testid={`plan-${p.key}`}>
                            <p className="text-xs uppercase tracking-wider text-sage-dark">{p.label}</p>
                            <div className="mt-2 flex items-baseline gap-1">
                                <span className="font-display text-3xl font-bold">{priceMain}</span>
                                <span className="text-sm text-inkSoft">{isUsd ? '/mo' : 'kr/mnd'}</span>
                            </div>
                            <p className="text-sm text-inkSoft mt-1">{p.tagline}</p>
                            <ul className="text-sm text-ink mt-4 space-y-1.5">
                                {p.key === 'basic' ? (
                                    <>
                                        <li className="flex items-start gap-2"><Check className="w-4 h-4 text-sage-dark shrink-0 mt-0.5"/> {t('settings.plan.basic.feat1')}</li>
                                        <li className="flex items-start gap-2"><Check className="w-4 h-4 text-sage-dark shrink-0 mt-0.5"/> {t('settings.plan.basic.feat2')}</li>
                                        <li className="flex items-start gap-2"><Check className="w-4 h-4 text-sage-dark shrink-0 mt-0.5"/> {t('settings.plan.basic.feat3')}</li>
                                    </>
                                ) : (
                                    <>
                                        <li className="flex items-start gap-2"><Check className="w-4 h-4 text-sage-dark shrink-0 mt-0.5"/> {t('settings.plan.premium.feat1')}</li>
                                        <li className="flex items-start gap-2"><Check className="w-4 h-4 text-sage-dark shrink-0 mt-0.5"/> {t('settings.plan.premium.feat2')}</li>
                                        <li className="flex items-start gap-2"><Check className="w-4 h-4 text-sage-dark shrink-0 mt-0.5"/> {t('settings.plan.premium.feat3')}</li>
                                    </>
                                )}
                            </ul>
                            <Button
                                onClick={() => startCheckout(p.key)}
                                disabled={active || paying === p.key}
                                className="mt-5 w-full rounded-full bg-sage hover:bg-sage-dark text-bone"
                                data-testid={`checkout-${p.key}`}
                            >
                                {active ? t('settings.plan.active.label') : paying === p.key ? t('settings.plan.opening') : <><Sparkles className="w-4 h-4 mr-2"/> {t('settings.plan.choose')} {p.label}</>}
                            </Button>
                        </div>
                    );
                })}
            </div>

            {/* Voice */}
            <h2 className="font-display text-lg font-semibold mt-10 flex items-center gap-2">
                <Volume2 className="w-4 h-4"/> {t('settings.voice.heading')}
            </h2>
            <p className="text-sm text-inkSoft mt-1">{t('settings.voice.subtitle')}</p>

            {[
                { key: 'female', label: t('settings.voice.female') },
                { key: 'male', label: t('settings.voice.male') },
                { key: 'neutral', label: t('settings.voice.neutral') },
            ].map(group => (
                <div key={group.key} className="mt-5">
                    <p className="text-xs uppercase tracking-wider text-inkSoft">{group.label}</p>
                    <div className="mt-2 grid sm:grid-cols-2 gap-2">
                        {groupedVoices[group.key].map(v => {
                            const selected = user?.tts_voice === v.key;
                            return (
                                <div key={v.key} className={`rounded-xl border p-3 flex items-center justify-between gap-2 ${selected ? 'border-sage bg-sage/5' : 'border-sand bg-white'}`} data-testid={`voice-${v.key}`}>
                                    <div className="min-w-0">
                                        <p className="font-medium text-ink">{v.label}</p>
                                        <p className="text-xs text-inkSoft truncate">{v.description}</p>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                        <button onClick={() => previewVoice(v.key)} className="p-2 rounded-full hover:bg-sand/40 text-inkSoft" data-testid={`preview-${v.key}`} title={t('settings.voice.preview')}>
                                            {previewing === v.key ? <span className="text-xs">▪</span> : <Volume2 className="w-4 h-4"/>}
                                        </button>
                                        <button
                                            onClick={() => setVoice(v.key)}
                                            disabled={selected}
                                            className={`text-xs rounded-full px-3 py-1 ${selected ? 'bg-sage text-bone' : 'bg-sand/60 text-ink hover:bg-sand'}`}
                                            data-testid={`select-${v.key}`}
                                        >
                                            {selected ? t('settings.voice.selected') : t('settings.voice.select')}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}

            <div className="mt-12">
                <Button onClick={async () => { await logout(); navigate('/'); }} variant="outline" className="rounded-full border-sand text-crisis hover:bg-crisis/5" data-testid="settings-logout">
                    <LogOut className="w-4 h-4 mr-2"/> {t('settings.logout')}
                </Button>
            </div>

            {user?.auth_method === 'email' && <ChangePasswordCard mustChange={false} onChanged={refresh}/>}
            <DataPrivacyCard onDeleted={async () => { await logout(); navigate('/'); }} />
        </div>
    );
}

function ChangePasswordCard({ mustChange, onChanged }) {
    const { t } = useT();
    const [current, setCurrent] = useState('');
    const [next, setNext] = useState('');
    const [confirm, setConfirm] = useState('');
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (next.length < 6) { toast.error(t('password.too.short')); return; }
        if (next !== confirm) { toast.error(t('password.mismatch')); return; }
        setBusy(true);
        try {
            await api.post('/me/password', mustChange
                ? { new_password: next }
                : { current_password: current, new_password: next });
            toast.success(t('password.changed'));
            setCurrent(''); setNext(''); setConfirm('');
            await onChanged();
        } catch (err) {
            toast.error(err.response?.data?.detail || t('password.change.error'));
        } finally { setBusy(false); }
    };

    return (
        <div className={`mt-12 pt-8 ${mustChange ? '' : 'border-t border-sand'}`} data-testid="change-password-card">
            <h2 className="font-display text-lg font-semibold flex items-center gap-2">
                <Lock className="w-4 h-4"/> {t('password.section.title')}
            </h2>
            {mustChange && (
                <p className="text-sm text-crisis mt-2 leading-relaxed">{t('password.must.change.body')}</p>
            )}
            <form onSubmit={submit} className="mt-4 space-y-2 max-w-sm">
                {!mustChange && (
                    <input
                        type="password"
                        required
                        autoComplete="current-password"
                        placeholder={t('password.current')}
                        value={current}
                        onChange={e => setCurrent(e.target.value)}
                        className="w-full rounded-lg border border-sand bg-white px-3 py-2 text-sm focus:outline-none focus:border-sage"
                        data-testid="password-current-input"
                    />
                )}
                <input
                    type="password"
                    required
                    minLength={6}
                    autoComplete="new-password"
                    placeholder={t('password.new')}
                    value={next}
                    onChange={e => setNext(e.target.value)}
                    className="w-full rounded-lg border border-sand bg-white px-3 py-2 text-sm focus:outline-none focus:border-sage"
                    data-testid="password-new-input"
                />
                <input
                    type="password"
                    required
                    minLength={6}
                    autoComplete="new-password"
                    placeholder={t('password.confirm')}
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    className="w-full rounded-lg border border-sand bg-white px-3 py-2 text-sm focus:outline-none focus:border-sage"
                    data-testid="password-confirm-input"
                />
                <Button
                    type="submit"
                    disabled={busy}
                    size="sm"
                    className="rounded-full bg-sage hover:bg-sage-dark text-bone"
                    data-testid="password-submit-btn"
                >
                    {busy ? t('password.busy') : t('password.submit')}
                </Button>
            </form>
        </div>
    );
}

function DataPrivacyCard({ onDeleted }) {
    const { t } = useT();
    const [exporting, setExporting] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [confirmText, setConfirmText] = useState('');
    const [deleting, setDeleting] = useState(false);

    const exportData = async () => {
        setExporting(true);
        try {
            const token = getToken();
            const res = await fetch(`${API}/account/export`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error(t('privacy.export.error'));
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const ts = new Date().toISOString().slice(0, 10);
            a.download = `stillerom-eksport-${ts}.json`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success(t('privacy.export.success'));
        } catch (e) {
            toast.error(e.message || t('privacy.export.error'));
        } finally {
            setExporting(false);
        }
    };

    const confirmDelete = async () => {
        const expected = t('privacy.delete.confirm.word');
        if (confirmText !== expected) {
            toast.error(t('privacy.delete.confirm.error'));
            return;
        }
        setDeleting(true);
        try {
            await api.delete('/account', { data: { confirm: 'SLETT' } });
            toast.success(t('privacy.delete.success'));
            setDeleteOpen(false);
            await onDeleted();
        } catch (err) {
            toast.error(err.response?.data?.detail || t('privacy.delete.error'));
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="mt-12 pt-8 border-t border-sand" data-testid="privacy-card">
            <h2 className="font-display text-lg font-semibold">{t('privacy.title')}</h2>
            <p className="text-sm text-inkSoft mt-2 leading-relaxed">
                {t('privacy.body')}
            </p>

            <div className="mt-4 flex flex-wrap gap-3">
                <Button
                    onClick={exportData}
                    disabled={exporting}
                    variant="outline"
                    className="rounded-full border-sand"
                    data-testid="export-data-btn"
                >
                    {exporting ? t('privacy.export.busy') : t('privacy.export.btn')}
                </Button>
                {!deleteOpen ? (
                    <Button
                        onClick={() => setDeleteOpen(true)}
                        variant="outline"
                        className="rounded-full border-crisis/40 text-crisis hover:bg-crisis/5"
                        data-testid="delete-account-open-btn"
                    >
                        {t('privacy.delete.btn')}
                    </Button>
                ) : null}
            </div>

            {deleteOpen && (
                <div className="mt-5 rounded-2xl border border-crisis/30 bg-crisis/5 p-5" data-testid="delete-account-form">
                    <p className="text-sm font-medium text-crisis">{t('privacy.delete.warn')}</p>
                    <p className="text-xs text-inkSoft mt-2 leading-relaxed">
                        {t('privacy.delete.body')}
                    </p>
                    <p className="text-xs text-inkSoft mt-3" dangerouslySetInnerHTML={{__html: t('privacy.delete.type').replace(/'([^']+)'/, '<strong>$1</strong>')}} />
                    <input
                        type="text"
                        value={confirmText}
                        onChange={e => setConfirmText(e.target.value)}
                        className="mt-2 w-full rounded-lg border border-sand bg-white px-3 py-2 text-sm focus:outline-none focus:border-crisis"
                        data-testid="delete-confirm-input"
                        placeholder={t('privacy.delete.placeholder')}
                    />
                    <div className="mt-3 flex gap-2">
                        <Button
                            onClick={confirmDelete}
                            disabled={deleting || confirmText !== t('privacy.delete.confirm.word')}
                            className="rounded-full bg-crisis hover:bg-crisis/90 text-bone"
                            size="sm"
                            data-testid="delete-confirm-btn"
                        >
                            {deleting ? t('privacy.delete.busy') : t('privacy.delete.confirm.btn')}
                        </Button>
                        <Button
                            onClick={() => { setDeleteOpen(false); setConfirmText(''); }}
                            variant="ghost"
                            size="sm"
                            className="rounded-full text-inkSoft"
                            data-testid="delete-cancel-btn"
                        >
                            {t('common.cancel')}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

function LinkEmailCard({ onLinked }) {
    const { t } = useT();
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [alias, setAlias] = useState('');
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 6) { toast.error(t('linkemail.password.short')); return; }
        setBusy(true);
        try {
            await api.post('/auth/link-email', { email, password, alias: alias || undefined });
            toast.success(t('linkemail.success'));
            setOpen(false);
            setEmail(''); setPassword(''); setAlias('');
            await onLinked();
        } catch (err) {
            toast.error(err.response?.data?.detail || t('linkemail.error'));
        } finally { setBusy(false); }
    };

    return (
        <div className="mt-4 bg-sage/5 border border-sage/20 rounded-xl p-4" data-testid="link-email-card">
            <p className="text-sm font-medium text-ink">{t('linkemail.title')}</p>
            <p className="text-xs text-inkSoft mt-1 leading-relaxed">
                {t('linkemail.body')}
            </p>
            {!open ? (
                <Button
                    onClick={() => setOpen(true)}
                    size="sm"
                    variant="outline"
                    className="mt-3 rounded-full border-sage/40 text-sage-dark hover:bg-sage/10"
                    data-testid="link-email-open-btn"
                >
                    {t('linkemail.open.btn')}
                </Button>
            ) : (
                <form onSubmit={submit} className="mt-3 space-y-2" data-testid="link-email-form">
                    <input
                        type="text"
                        placeholder={t('linkemail.alias')}
                        value={alias}
                        onChange={e => setAlias(e.target.value)}
                        className="w-full rounded-lg border border-sand bg-white px-3 py-2 text-sm focus:outline-none focus:border-sage"
                        data-testid="link-email-alias"
                    />
                    <input
                        type="email"
                        required
                        placeholder={t('linkemail.email')}
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        className="w-full rounded-lg border border-sand bg-white px-3 py-2 text-sm focus:outline-none focus:border-sage"
                        data-testid="link-email-email"
                    />
                    <input
                        type="password"
                        required
                        minLength={6}
                        placeholder={t('linkemail.password')}
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        className="w-full rounded-lg border border-sand bg-white px-3 py-2 text-sm focus:outline-none focus:border-sage"
                        data-testid="link-email-password"
                    />
                    <div className="flex gap-2">
                        <Button
                            type="submit"
                            disabled={busy}
                            size="sm"
                            className="rounded-full bg-sage hover:bg-sage-dark text-bone"
                            data-testid="link-email-submit-btn"
                        >
                            {busy ? t('linkemail.busy') : t('common.save')}
                        </Button>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="rounded-full text-inkSoft"
                            onClick={() => setOpen(false)}
                            data-testid="link-email-cancel-btn"
                        >
                            {t('common.cancel')}
                        </Button>
                    </div>
                </form>
            )}
        </div>
    );
}
